/**
 * Treppan Academy - Learning Paths
 * Structured sequences of courses for guided training
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Route, BookOpen, Clock, Award, ChevronRight, Play,
  Users, TrendingUp, Star, Zap, Target, GraduationCap
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  purple: '#A855F7',
  blue: '#3B82F6',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const difficultyColors = {
  beginner: colors.success,
  intermediate: colors.gold,
  advanced: colors.purple
};

const PathCard = ({ path, onEnroll }) => {
  const navigate = useNavigate();
  const progress = path.user_progress;
  const isEnrolled = !!progress;
  const progressPercent = progress?.progress_percent || 0;

  return (
    <Card 
      className="group cursor-pointer transition-all hover:scale-[1.02]"
      style={{ 
        backgroundColor: colors.surface, 
        border: `1px solid ${colors.border}`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}
      onClick={() => navigate(`/academy/paths/${path.id}`)}
      data-testid={`path-card-${path.id}`}
    >
      <CardContent className="p-0">
        {/* Thumbnail */}
        <div className="relative h-40 overflow-hidden rounded-t-xl">
          <img 
            src={path.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600'}
            alt={path.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge 
              style={{ 
                backgroundColor: `${difficultyColors[path.difficulty]}20`,
                color: difficultyColors[path.difficulty]
              }}
            >
              {path.difficulty}
            </Badge>
            {isEnrolled && (
              <Badge style={{ backgroundColor: `${colors.gold}20`, color: colors.gold }}>
                <Play className="h-3 w-3 mr-1" />
                In Progress
              </Badge>
            )}
          </div>
          
          {/* Course count */}
          <div className="absolute bottom-3 right-3">
            <Badge style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: colors.textHeading }}>
              <BookOpen className="h-3 w-3 mr-1" />
              {path.courses?.length || 0} courses
            </Badge>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-5">
          <h3 
            className="font-bold text-lg mb-2 line-clamp-2"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            {path.title}
          </h3>
          
          <p 
            className="text-sm mb-4 line-clamp-2"
            style={{ color: colors.textBody }}
          >
            {path.description}
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" style={{ color: colors.textMuted }} />
              <span className="text-sm" style={{ color: colors.textMuted }}>
                {path.estimated_hours}h
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Route className="h-4 w-4" style={{ color: colors.textMuted }} />
              <span className="text-sm" style={{ color: colors.textMuted }}>
                {path.courses?.length || 0} courses
              </span>
            </div>
          </div>
          
          {/* Progress or Enroll */}
          {isEnrolled ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: colors.textBody }}>Progress</span>
                <span className="text-sm font-medium" style={{ color: colors.gold }}>
                  {progressPercent}%
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          ) : (
            <Button 
              className="w-full"
              style={{ backgroundColor: colors.gold, color: '#000' }}
              onClick={(e) => { e.stopPropagation(); onEnroll(path.id); }}
              data-testid={`enroll-path-${path.id}`}
            >
              <Play className="h-4 w-4 mr-2" />
              Start Learning Path
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function LearningPaths() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [myPaths, setMyPaths] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Progressive — catalog and my-paths render independently.
  const fetchData = () => {
    const tasks = [
      fetch(`${API_URL}/api/academy/paths?token=${token}`)
        .then((r) => r.json())
        .then((d) => d?.success && setPaths(d.paths || []))
        .catch((e) => console.error('[LearningPaths] paths failed:', e)),
      fetch(`${API_URL}/api/academy/my-paths?token=${token}`)
        .then((r) => r.json())
        .then((d) => d?.success && setMyPaths(d.paths || []))
        .catch((e) => console.error('[LearningPaths] my-paths failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const handleEnroll = async (pathId) => {
    try {
      const res = await fetch(`${API_URL}/api/academy/paths/${pathId}/enroll?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Enrolled in learning path!');
        fetchData(); // Refresh data
        navigate(`/academy/paths/${pathId}`);
      } else {
        toast.error(data.error || 'Failed to enroll');
      }
    } catch (err) {
      toast.error('Failed to enroll in learning path');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.gold }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: colors.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              Learning Paths
            </h1>
            <p style={{ color: colors.textBody }}>
              Structured course sequences for comprehensive skill development
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/academy')}
            variant="outline"
            data-testid="back-to-academy"
          >
            <GraduationCap className="h-4 w-4 mr-2" />
            Academy Home
          </Button>
        </div>

        {/* My Learning Paths */}
        {myPaths.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${colors.gold}20` }}
              >
                <TrendingUp className="h-5 w-5" style={{ color: colors.gold }} />
              </div>
              <div>
                <h2 
                  className="text-xl font-bold"
                  style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                >
                  Continue Your Journey
                </h2>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  {myPaths.length} active learning path{myPaths.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myPaths.map(path => (
                <PathCard key={path.id} path={path} onEnroll={handleEnroll} />
              ))}
            </div>
          </div>
        )}

        {/* All Learning Paths */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${colors.purple}20` }}
            >
              <Route className="h-5 w-5" style={{ color: colors.purple }} />
            </div>
            <div>
              <h2 
                className="text-xl font-bold"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                Explore Learning Paths
              </h2>
              <p className="text-sm" style={{ color: colors.textMuted }}>
                {paths.length} path{paths.length !== 1 ? 's' : ''} available
              </p>
            </div>
          </div>
          
          {paths.length === 0 ? (
            <Card 
              className="p-12 text-center"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
              <Route className="h-16 w-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
              <p className="text-lg mb-2" style={{ color: colors.textHeading }}>
                No Learning Paths Available
              </p>
              <p style={{ color: colors.textMuted }}>
                Check back soon for structured learning experiences
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paths.filter(p => !myPaths.find(mp => mp.id === p.id)).map(path => (
                <PathCard key={path.id} path={path} onEnroll={handleEnroll} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
